import React, { useEffect, useMemo, useState } from "react";
import { Search, ShieldCheck, Clock, Trophy, Loader2 } from "lucide-react";
import { fetchPlayers } from "../lib/api";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";

const SCORE_RANGES = [
    { label: "All Scores", value: "all", min: null, max: null },
    { label: "Sub-260 (Elite)", value: "elite", min: 0, max: 259 },
    { label: "260–275", value: "mid-low", min: 260, max: 275 },
    { label: "276–290", value: "mid", min: 276, max: 290 },
    { label: "291+", value: "high", min: 291, max: 9999 },
];

const rankStyle = (i) => {
    if (i === 0) return "text-[#D4AF37]";
    if (i === 1) return "text-slate-200";
    if (i === 2) return "text-amber-600";
    return "text-[#94A3B8]";
};

const StatusBadge = ({ status }) => {
    if (status === "verified") {
        return (
            <span className="inline-flex items-center gap-1.5 bg-[#FF5A00]/15 text-[#FF5A00] text-[10px] font-bold px-2 py-1 uppercase tracking-[0.2em] border border-[#FF5A00]/40">
                <ShieldCheck size={10} />
                Verified
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 bg-slate-500/15 text-slate-300 text-[10px] font-bold px-2 py-1 uppercase tracking-[0.2em] border border-slate-500/40">
            <Clock size={10} />
            Pending
        </span>
    );
};

const LeaderRow = ({ p, index }) => {
    const rankNumber = index + 1;
    return (
        <tr
            data-testid={`leaderboard-row-${p.trackman_id}`}
            className="border-b border-white/5 hover:bg-white/[0.03] transition-colors fade-up"
            style={{ animationDelay: `${Math.min(index * 30, 600)}ms` }}
        >
            <td className="py-4 pl-6 pr-3">
                <div className="flex items-center gap-2">
                    {rankNumber <= 3 && <Trophy size={12} className={rankStyle(index)} />}
                    <span className={`font-mono-num font-bold text-sm ${rankStyle(index)}`}>
                        {String(rankNumber).padStart(2, "0")}
                    </span>
                </div>
            </td>
            <td className="py-4 px-3">
                <div className="text-white font-medium text-sm">{p.name}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#94A3B8] font-mono-num">
                    {p.trackman_id} {p.country && <span className="text-[#475569]">· {p.country}</span>}
                </div>
            </td>
            <td className="py-4 px-3 text-right">
                <span className="font-mono-num font-bold text-[#D4AF37] text-base">
                    {p.total_aggregate}
                </span>
            </td>
            <td className="py-4 px-3 text-right hidden md:table-cell">
                <span className="font-mono-num text-white/90 text-sm">{p.bethpage_black}</span>
            </td>
            <td className="py-4 px-3 text-right hidden md:table-cell">
                <span className="font-mono-num text-white/90 text-sm">{p.pebble_beach}</span>
            </td>
            <td className="py-4 px-3 text-right hidden md:table-cell">
                <span className="font-mono-num text-white/90 text-sm">{p.oakmont}</span>
            </td>
            <td className="py-4 px-3 text-right hidden md:table-cell">
                <span className="font-mono-num text-white/90 text-sm">{p.pinehurst}</span>
            </td>
            <td className="py-4 px-3 pr-6 text-right">
                <StatusBadge status={p.status} />
            </td>
        </tr>
    );
};

const LeaderTable = ({ players, loading, division }) => {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-20" data-testid="leaderboard-loading">
                <Loader2 className="text-[#D4AF37] animate-spin" size={28} />
            </div>
        );
    }
    if (!players.length) {
        return (
            <div
                className="text-center py-20 text-[#94A3B8] text-sm"
                data-testid="leaderboard-empty"
            >
                No players match the current filters.
            </div>
        );
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full" data-testid={`leaderboard-table-${division}`}>
                <thead>
                    <tr className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-[0.2em] border-b border-white/10 bg-white/[0.02]">
                        <th className="py-3 pl-6 pr-3 text-left">Rank</th>
                        <th className="py-3 px-3 text-left">Player / Trackman ID</th>
                        <th className="py-3 px-3 text-right">Total</th>
                        <th className="py-3 px-3 text-right hidden md:table-cell">Bethpage</th>
                        <th className="py-3 px-3 text-right hidden md:table-cell">Pebble</th>
                        <th className="py-3 px-3 text-right hidden md:table-cell">Oakmont</th>
                        <th className="py-3 px-3 text-right hidden md:table-cell">Pinehurst</th>
                        <th className="py-3 px-3 pr-6 text-right">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {players.map((p, i) => (
                        <LeaderRow key={p.id} p={p} index={i} />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export const Leaderboard = () => {
    const [division, setDivision] = useState("mens");
    const [search, setSearch] = useState("");
    const [range, setRange] = useState("all");
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);

    const rangeObj = useMemo(
        () => SCORE_RANGES.find((r) => r.value === range) || SCORE_RANGES[0],
        [range]
    );

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const data = await fetchPlayers({
                    division,
                    search: search.trim() || undefined,
                    minScore: rangeObj.min,
                    maxScore: rangeObj.max,
                });
                if (!cancelled) setPlayers(data);
            } catch (e) {
                console.error("fetchPlayers failed", e);
                if (!cancelled) setPlayers([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 220);

        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [division, search, rangeObj]);

    return (
        <section
            id="leaderboard"
            className="relative border-b border-white/10 py-20 md:py-28 bg-[#0B1325]"
            data-testid="leaderboard-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <div className="text-[11px] tracking-[0.4em] uppercase font-bold text-[#D4AF37] mb-3">
                            — Section 02 / Global Leaderboard
                        </div>
                        <h2 className="font-display font-black uppercase text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-none">
                            The Hunt
                            <br />
                            <span className="text-[#94A3B8]">In Progress.</span>
                        </h2>
                    </div>
                    <p className="text-[#94A3B8] text-sm max-w-md leading-relaxed">
                        Aggregate of each player&apos;s single lowest 18-hole round across all four
                        majors. Lower is better. Ties broken by most recent verified round.
                    </p>
                </div>

                <Tabs value={division} onValueChange={setDivision} className="w-full">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                        <TabsList
                            className="bg-transparent p-0 h-auto gap-0 border border-white/10 rounded-none"
                            data-testid="leaderboard-tabs"
                        >
                            <TabsTrigger
                                value="mens"
                                data-testid="leaderboard-tab-mens"
                                className="rounded-none px-6 py-3 text-xs uppercase tracking-[0.25em] font-bold data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black data-[state=inactive]:text-[#94A3B8] data-[state=inactive]:hover:text-white transition-colors"
                            >
                                Men&apos;s Division
                            </TabsTrigger>
                            <TabsTrigger
                                value="womens"
                                data-testid="leaderboard-tab-womens"
                                className="rounded-none px-6 py-3 text-xs uppercase tracking-[0.25em] font-bold data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black data-[state=inactive]:text-[#94A3B8] data-[state=inactive]:hover:text-white transition-colors"
                            >
                                Women&apos;s Division
                            </TabsTrigger>
                        </TabsList>

                        <div className="flex flex-col sm:flex-row gap-3 lg:items-center">
                            <div className="relative">
                                <Search
                                    size={14}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]"
                                />
                                <Input
                                    data-testid="leaderboard-search"
                                    placeholder="Search Trackman ID or name"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-9 w-full sm:w-72 bg-white/[0.03] border-white/10 rounded-none text-white placeholder:text-[#475569] focus-visible:ring-[#D4AF37]/40 focus-visible:border-[#D4AF37]/50 text-sm font-mono-num"
                                />
                            </div>
                            <Select value={range} onValueChange={setRange}>
                                <SelectTrigger
                                    data-testid="leaderboard-range-trigger"
                                    className="w-full sm:w-52 bg-white/[0.03] border-white/10 rounded-none text-white text-xs uppercase tracking-[0.2em] font-bold focus:ring-[#D4AF37]/40"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent
                                    data-testid="leaderboard-range-content"
                                    className="bg-[#111827] border-white/10 rounded-none text-white"
                                >
                                    {SCORE_RANGES.map((r) => (
                                        <SelectItem
                                            key={r.value}
                                            value={r.value}
                                            data-testid={`leaderboard-range-${r.value}`}
                                            className="text-xs uppercase tracking-[0.2em] font-bold focus:bg-[#D4AF37]/15 focus:text-white"
                                        >
                                            {r.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="border border-white/10 bg-[#0B1325]">
                        <TabsContent value="mens" className="m-0">
                            <LeaderTable players={players} loading={loading} division="mens" />
                        </TabsContent>
                        <TabsContent value="womens" className="m-0">
                            <LeaderTable players={players} loading={loading} division="womens" />
                        </TabsContent>
                    </div>
                </Tabs>

                <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-[#475569]">
                    Showing {players.length} player{players.length === 1 ? "" : "s"} ·{" "}
                    {division === "mens" ? "Men\u2019s" : "Women\u2019s"} division
                </div>
            </div>
        </section>
    );
};

export default Leaderboard;
